/**
 * Device Fingerprint & Identity Module
 *
 * Implements Strategy 3 (Hybrid): UUID + Browser Fingerprint
 *
 * - UUID: Stored in localStorage. If missing → new device.
 * - Fingerprint: A hash of multiple browser/hardware signals that are hard to
 *   replicate on another machine. Even if someone copies the UUID, the backend
 *   can detect a fingerprint mismatch and flag the request.
 *
 * Both values are sent as custom HTTP headers on every API request:
 *   X-Device-Id:          <uuid>
 *   X-Device-Fingerprint: <sha256-hash>
 */

const DEVICE_ID_KEY = "radarplus_device_id";

// ─── UUID Management ─────────────────────────────────────────────────────────

/**
 * Get or create a persistent device UUID.
 * Returns `{ deviceId, isNewDevice }`.
 */
export function getOrCreateDeviceId(): { deviceId: string; isNewDevice: boolean } {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) {
    return { deviceId: existing, isNewDevice: false };
  }

  const newId = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, newId);
  return { deviceId: newId, isNewDevice: true };
}

/**
 * Read the current device ID without creating one (returns null if none).
 */
export function getDeviceId(): string | null {
  return localStorage.getItem(DEVICE_ID_KEY);
}

// ─── Browser Fingerprint ─────────────────────────────────────────────────────

/**
 * Collect raw fingerprint components from the browser environment.
 * These signals are combined and hashed to produce a device fingerprint.
 */
function collectFingerprintComponents(): string[] {
  const components: string[] = [];

  // 1. User Agent
  components.push(navigator.userAgent);

  // 2. Screen properties
  components.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);

  // 3. Timezone
  try {
    components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    components.push("tz-unknown");
  }

  // 4. Hardware concurrency (number of CPU cores)
  components.push(navigator.hardwareConcurrency?.toString() ?? "cpu-unknown");

  // 5. Device memory (GB, Chrome-only)
  components.push((navigator as any).deviceMemory?.toString() ?? "mem-unknown");

  // 6. Languages
  components.push(navigator.languages?.join(",") ?? navigator.language ?? "lang-unknown");

  // 7. Platform
  components.push(navigator.platform ?? "platform-unknown");

  // 8. Touch support
  components.push(`touch:${navigator.maxTouchPoints ?? 0}`);

  // 9. Canvas fingerprint — render a specific image and hash the pixel data
  try {
    components.push(getCanvasFingerprint());
  } catch {
    components.push("canvas-unavailable");
  }

  // 10. WebGL renderer string — identifies the GPU
  try {
    components.push(getWebGLRenderer());
  } catch {
    components.push("webgl-unavailable");
  }

  return components;
}

/**
 * Generate a canvas-based fingerprint.
 * Different GPUs / OS render text slightly differently at the sub-pixel level.
 */
function getCanvasFingerprint(): string {
  const canvas = document.createElement("canvas");
  canvas.width = 200;
  canvas.height = 50;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "canvas-no-ctx";

  // Draw text with specific styling — the rendering varies by hardware
  ctx.textBaseline = "top";
  ctx.font = "14px 'Arial'";
  ctx.fillStyle = "#f60";
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = "#069";
  ctx.fillText("RadarPlus,FP!🎯", 2, 15);
  ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
  ctx.fillText("RadarPlus,FP!🎯", 4, 17);

  return canvas.toDataURL();
}

/**
 * Extract the WebGL renderer string which identifies the GPU model.
 */
function getWebGLRenderer(): string {
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl");
  if (!gl) return "webgl-unsupported";

  const glContext = gl as WebGLRenderingContext;
  const debugInfo = glContext.getExtension("WEBGL_debug_renderer_info");
  if (!debugInfo) return "webgl-no-debug";

  const vendor = glContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) ?? "unknown-vendor";
  const renderer = glContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) ?? "unknown-renderer";

  // Clean up the GL context
  const loseCtx = glContext.getExtension("WEBGL_lose_context");
  loseCtx?.loseContext();

  return `${vendor}~${renderer}`;
}

// ─── Hashing ─────────────────────────────────────────────────────────────────

/**
 * Hash a string with SHA-256 using the Web Crypto API.
 */
async function sha256(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Generate the browser fingerprint hash.
 * This should be called once at app startup and cached for the session.
 */
export async function generateFingerprint(): Promise<string> {
  const components = collectFingerprintComponents();
  const raw = components.join("|");
  return sha256(raw);
}

/**
 * Cached fingerprint so we don't regenerate on every request.
 */
let cachedFingerprint: string | null = null;

/**
 * Get the device fingerprint (generates once, then caches in memory).
 */
export async function getFingerprint(): Promise<string> {
  cachedFingerprint ??= await generateFingerprint();
  return cachedFingerprint;
}

/**
 * Get both device identity values for sending to the backend.
 * Returns `{ deviceId, fingerprint, isNewDevice }`.
 */
export async function getDeviceIdentity(): Promise<{
  deviceId: string;
  fingerprint: string;
  isNewDevice: boolean;
}> {
  const { deviceId, isNewDevice } = getOrCreateDeviceId();
  const fingerprint = await getFingerprint();
  return { deviceId, fingerprint, isNewDevice };
}
